import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { langfuseApi, type Trace } from '@/api/langfuse.api';
import { useCursorPager } from '@/hooks/useCursorPager';
import { TraceSearchBar } from '@/components/TraceSearchBar';
import {
  EMPTY_TRACE_SEARCH,
  traceSearchParams,
  type TraceSearch,
} from '@/components/traceSearch';
import { listAgentInstanceOptions } from './instanceOptions.api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ChevronLeftIcon, ChevronRightIcon } from '@/components/icons';
import { TraceDetailsDialog } from '@/components/TraceDetailsDialog';
import { TraceIOCell } from '@/components/TraceIOCell';
import { TraceTableError, TraceTableSkeleton } from '@/components/TraceTableStates';
import { formatTimestampISO } from '@/utils/trace.utils';

export default function AgentTrace() {
  const { id: agentId } = useParams<{ id: string }>();
  const pager = useCursorPager();
  const [pageSize, setPageSize] = useState(10);
  const [selectedTraceId, setSelectedTraceId] = useState<string | null>(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [search, setSearch] = useState<TraceSearch>(EMPTY_TRACE_SEARCH);

  const { data: instanceOptions } = useQuery({
    queryKey: ['agent-trace-instance-options', agentId],
    queryFn: () => listAgentInstanceOptions(agentId!),
    enabled: !!agentId,
  });

  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ['agent-traces', agentId, search, pager.cursor, pageSize],
    queryFn: () => {
      const tags = agentId ? [`agent=${agentId}`] : [];
      if (search.instanceId) tags.push(`instance=${search.instanceId}`);
      return langfuseApi.listTraces({
        tags,
        pageSize,
        cursor: pager.cursor,
        withTotal: pager.isFirstPage,
        ...traceSearchParams(search),
      });
    },
    enabled: !!agentId,
  });

  const handleSearchChange = (next: TraceSearch) => {
    setSearch(next);
    pager.reset();
  };
  const isFiltered =
    search.hours !== null || !!search.keyword || !!search.instanceId;

  const { setTotal } = pager;
  useEffect(() => {
    if (data?.total !== undefined) setTotal(data.total);
  }, [data?.total, setTotal]);

  const traces = data?.items ?? [];
  const totalLabel = pager.total !== undefined ? `约 ${pager.total} 条记录` : '';

  const handleTraceClick = (traceId: string) => {
    setSelectedTraceId(traceId);
    setDetailsDialogOpen(true);
  };

  return (
    <div className="space-y-6">
      {/* <div>
        <h1 className="text-2xl font-bold tracking-tight">Trace</h1>
        <p className="text-muted-foreground mt-1">Agent 调用链路追踪</p>
      </div> */}

      <Card>
        <CardHeader>
          <CardTitle>调用记录</CardTitle>
          <CardDescription>{totalLabel}</CardDescription>
          <div className="pt-2">
            <TraceSearchBar
              value={search}
              onChange={handleSearchChange}
              instances={instanceOptions ?? []}
            />
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <TraceTableSkeleton rows={pageSize > 10 ? 10 : 5} />
          ) : error ? (
            <TraceTableError />
          ) : traces.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <svg
                className="w-16 h-16 mx-auto mb-4 opacity-50"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2"
                />
              </svg>
              <p className="text-lg font-medium mb-2">
                {data?.hasMore
                  ? '本页无可显示的记录'
                  : isFiltered
                    ? '没有符合条件的调用记录'
                    : '暂无调用记录'}
              </p>
              <p className="text-sm">
                {data?.hasMore
                  ? '请翻到下一页继续查看'
                  : isFiltered
                    ? '请调整搜索条件后重试'
                    : '调用记录将在 Agent 被使用时显示'}
              </p>
            </div>
          ) : (
            <>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 border-b">
                    <tr>
                      <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground">
                        Trace ID
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground">
                        时间
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground">
                        Input
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground">
                        Output
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {traces.map((trace: Trace) => (
                      <tr
                        key={trace.id}
                        className="hover:bg-muted/30 cursor-pointer"
                        onClick={() => handleTraceClick(trace.id)}
                      >
                        <td className="px-3 py-3 font-mono text-xs max-w-[150px] truncate">
                          {trace.id}
                        </td>
                        <td className="px-3 py-3 text-muted-foreground">
                          {formatTimestampISO(trace.timestamp)}
                        </td>
                        <TraceIOCell value={trace.input} />
                        <TraceIOCell value={trace.output} />
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
          {/* 分页条在加载时保留，只有表格区域显示加载占位 */}
          {!error && (isLoading || traces.length > 0 || data?.hasMore || !pager.isFirstPage) && (
            <div className="flex items-center justify-between mt-4">
              <div className="flex items-center gap-4">
                <p className="text-sm text-muted-foreground">
                  第 {pager.page} 页{totalLabel ? `，${totalLabel}` : ''}
                </p>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">每页</span>
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      pager.reset();
                    }}
                    className="h-8 rounded-md border border-input bg-transparent px-2 py-1 text-sm hover:bg-accent hover:text-accent-foreground"
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                  <span className="text-sm text-muted-foreground">条</span>
                </div>
              </div>
              {(!pager.isFirstPage || data?.hasMore) && (
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={pager.prev}
                    disabled={pager.isFirstPage || isFetching}
                  >
                    <ChevronLeftIcon className="h-4 w-4 mr-1" />
                    上一页
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => pager.next(data?.nextCursor ?? '')}
                    disabled={!data?.hasMore || isFetching}
                  >
                    下一页
                    <ChevronRightIcon className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
      <TraceDetailsDialog
        traceId={selectedTraceId}
        open={detailsDialogOpen}
        onOpenChange={setDetailsDialogOpen}
      />
    </div>
  );
}
